import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { OrbitalSystem } from '../../components/ui/orbital-system';
import { ArrowLeft, ArrowRight, Loader2, Lock, Mail, Shield } from 'lucide-react';

/* ── Single choreographed entrance ────────────────────────────────── */

const entrance = {
  container: {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
  },
  item: {
    hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
    show: {
      opacity: 1, y: 0, filter: 'blur(0px)',
      transition: { duration: 0.55, ease: [0.21, 0.47, 0.32, 0.98] },
    },
  },
};

const panelReveal = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] },
  },
};

/* ── Page ─────────────────────────────────────────────────────────── */

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const responseData = await login(email, password);
      const role = responseData?.user?.role;
      const roleRedirects = {
        super_admin: '/dashboard',
        tenant_admin: '/dashboard',
        clerk: '/dashboard',
        faculty: '/dashboard/faculty/dashboard',
        student: '/dashboard',
        parent: '/dashboard',
      };
      navigate(roleRedirects[role] ?? '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Login failed. Please check your credentials.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">

      {/* ═══════════════════════════════════════════════════════════════
          LEFT PANEL — orbital system + brand
          ═══════════════════════════════════════════════════════════════ */}
      <motion.div
        variants={panelReveal}
        initial="hidden"
        animate="show"
        className="hidden lg:flex lg:w-[46%] flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(165deg, #1a3328 0%, #142a20 40%, #0f2018 100%)' }}
      >
        {/* ── Architectural grid lines ── */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-[38%] w-px h-full bg-white/[0.06]" />
          <div className="absolute top-0 left-[76%] w-px h-full bg-white/[0.04]" />
          <div className="absolute top-[55%] left-0 w-full h-px bg-white/[0.06]" />
        </div>

        {/* ── Ambient glow behind orbital ── */}
        <div className="absolute top-[15%] left-[15%] w-[55%] h-[45%] rounded-full bg-[#2a5c42]/15 blur-[120px]" />

        {/* ── "360" typographic watermark behind the orbital ── */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <span
            className="text-[20rem] font-extralight text-white/[0.07] tracking-tighter leading-none"
            style={{ fontFamily: 'Outfit' }}
          >
            360
          </span>
        </div>

        {/* ── Grain texture ── */}
        <div
          className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
          }}
        />

        {/* ── Content layer ── */}
        <motion.div
          className="relative z-10 flex flex-col h-full p-10 xl:p-14"
          variants={entrance.container}
          initial="hidden"
          animate="show"
        >
          {/* Logo — top */}
          <motion.div variants={entrance.item} className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-white/[0.08] border border-white/[0.1] flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-4 h-4 text-white/80" />
            </div>
            <div>
              <p className="text-base font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>CliniQ 360</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/30">Medical College Operations</p>
            </div>
          </motion.div>

          {/* ── Orbital System — center stage ── */}
          <div className="flex-1 relative my-4">
            <OrbitalSystem className="absolute inset-0" />
          </div>

          {/* Tagline + stats — bottom */}
          <div className="shrink-0 space-y-6">
            <motion.div variants={entrance.item}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-gradient-to-r from-white/25 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/30 font-medium">Operations Platform</span>
              </div>
              <h2
                className="text-[1.65rem] xl:text-[1.85rem] font-extralight text-white leading-[1.25] tracking-tight max-w-sm"
                style={{ fontFamily: 'Outfit' }}
              >
                Every module connects to one{' '}
                <span className="font-normal text-white/90">coherent operating layer</span>{' '}
                for your institution.
              </h2>
            </motion.div>

            <motion.div variants={entrance.item}>
              <div className="flex items-center gap-px rounded-xl overflow-hidden">
                {[
                  { val: '12', label: 'Modules' },
                  { val: '6', label: 'User roles' },
                  { val: '360°', label: 'Visibility' },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className={`flex-1 py-3 px-4 text-center bg-white/[0.04] ${i > 0 ? 'border-l border-white/[0.06]' : ''} hover:bg-white/[0.07] transition-colors duration-300`}
                  >
                    <p className="text-base font-semibold text-white/90 tabular-nums" style={{ fontFamily: 'Outfit' }}>{s.val}</p>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-white/30 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════
          RIGHT PANEL — form in a glass card
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 bg-[#F4F5F0] relative" data-testid="login-form-panel">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,_rgba(31,60,47,0.04),_transparent_70%)]" />

        <motion.div
          className="w-full max-w-[380px] relative z-10"
          variants={entrance.container}
          initial="hidden"
          animate="show"
        >
          {/* Mobile logo */}
          <motion.div variants={entrance.item} className="lg:hidden flex items-center gap-2.5 mb-12">
            <div className="w-9 h-9 rounded-xl bg-[#1F3C2F] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-foreground tracking-tight text-lg" style={{ fontFamily: 'Outfit' }}>CliniQ 360</span>
          </motion.div>

          {/* Heading */}
          <motion.div variants={entrance.item} className="mb-9">
            <p className="text-[11px] uppercase tracking-[0.2em] text-stone-400 font-medium mb-3">Sign in to continue</p>
            <h1
              className="text-[1.75rem] font-medium text-[#1A1C1A] tracking-tight leading-tight"
              style={{ fontFamily: 'Outfit' }}
            >
              Welcome back
            </h1>
          </motion.div>

          {/* ── Form card ── */}
          <motion.div
            variants={entrance.item}
            className="rounded-2xl border border-stone-200/70 bg-white/80 backdrop-blur-sm p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]"
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-card">
              <AnimatePresence mode="sync">
                {error && (
                  <motion.div
                    className="p-3 rounded-xl bg-red-50/80 border border-red-200/60 text-sm text-red-700 flex items-start gap-2"
                    data-testid="login-error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <Shield className="w-4 h-4 mt-0.5 shrink-0 text-red-400" />
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium text-stone-500 uppercase tracking-[0.06em]">Email address</Label>
                <div className="relative group">
                  <Mail className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-[15px] h-[15px] transition-colors duration-200 ${focused === 'email' ? 'text-[#1F3C2F]' : 'text-stone-350'}`} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused(null)}
                    placeholder="you@institution.edu"
                    required
                    className="h-12 pl-10 bg-[#F4F5F0]/60 border-stone-200/80 rounded-xl text-sm placeholder:text-stone-400 focus-visible:ring-1 focus-visible:ring-[#1F3C2F]/40 focus-visible:border-[#1F3C2F]/50 focus-visible:bg-white transition-all duration-200"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-medium text-stone-500 uppercase tracking-[0.06em]">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-[11px] text-stone-400 hover:text-[#1F3C2F] transition-colors font-medium"
                    data-testid="forgot-password-link"
                    tabIndex={-1}
                  >
                    Forgot?
                  </Link>
                </div>
                <div className="relative group">
                  <Lock className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-[15px] h-[15px] transition-colors duration-200 ${focused === 'password' ? 'text-[#1F3C2F]' : 'text-stone-350'}`} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused('password')}
                    onBlur={() => setFocused(null)}
                    placeholder="Enter password"
                    required
                    className="h-12 pl-10 bg-[#F4F5F0]/60 border-stone-200/80 rounded-xl text-sm placeholder:text-stone-400 focus-visible:ring-1 focus-visible:ring-[#1F3C2F]/40 focus-visible:border-[#1F3C2F]/50 focus-visible:bg-white transition-all duration-200"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                className="pt-1"
              >
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-sm font-medium bg-[#1F3C2F] hover:bg-[#2a4f3d] text-white shadow-[0_1px_2px_rgba(0,0,0,0.1),0_4px_16px_rgba(31,60,47,0.18),inset_0_1px_0_rgba(255,255,255,0.06)] hover:shadow-[0_1px_2px_rgba(0,0,0,0.1),0_8px_24px_rgba(31,60,47,0.22),inset_0_1px_0_rgba(255,255,255,0.08)] transition-all duration-300"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </motion.div>
            </form>
          </motion.div>

          {/* Back to home */}
          <motion.div variants={entrance.item} className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 text-[13px] text-stone-400 hover:text-[#1A1C1A] transition-colors group"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform duration-200" />
              Back to home
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
